import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, ModalityBadge } from "@/components/Shared";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Clock, ArrowLeftRight, Plus } from "lucide-react";
import { format } from "date-fns";

export default function TechRequests() {
  const { user } = useAuth();
  const [timeoff, setTimeoff] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [toOpen, setToOpen] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);

  const load = async () => {
    try {
      const start = format(new Date(), "yyyy-MM-dd");
      const [t, s, sh] = await Promise.all([
        api.get("/timeoff"), api.get("/swaps"), api.get(`/shifts?start=${start}&end=2100-01-01`),
      ]);
      setTimeoff(t.data); setSwaps(s.data); setMyShifts(sh.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const decideSwap = async (id, status) => {
    try { await api.patch(`/swaps/${id}`, { status }); toast.success(`Swap ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="My Requests" subtitle="// Time-off & shift swaps">
        <div className="flex gap-2">
          <button data-testid="new-timeoff-btn" onClick={() => setToOpen(true)}
            className="flex items-center gap-2 px-3 py-2 border border-[#00F0FF] bg-[#00F0FF]/10 text-white text-xs font-mono-rs uppercase tracking-wider hover:bg-[#00F0FF]/25 transition-all">
            <Plus className="w-4 h-4" /> Time-off
          </button>
          <button data-testid="new-swap-btn" onClick={() => setSwapOpen(true)}
            className="flex items-center gap-2 px-3 py-2 border border-[#D902EE] bg-[#D902EE]/10 text-white text-xs font-mono-rs uppercase tracking-wider hover:bg-[#D902EE]/25 transition-all">
            <Plus className="w-4 h-4" /> Swap
          </button>
        </div>
      </PageHeader>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h3 className="font-mono-rs text-sm tracking-[0.2em] uppercase text-slate-400 mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-[#00F0FF]" /> Time-off</h3>
          <div className="space-y-3" data-testid="my-timeoff-list">
            {timeoff.length === 0 && <p className="text-slate-500 text-sm">No time-off requests yet.</p>}
            {timeoff.map((r) => (
              <div key={r.id} data-testid={`my-timeoff-${r.id}`} className="bg-[#0F1219] border border-slate-800 border-l-2 border-l-[#00F0FF] p-4">
                <div className="flex items-center justify-between">
                  <span className="font-mono-rs text-sm text-white">{r.start_date} → {r.end_date}</span>
                  <StatusBadge status={r.status} />
                </div>
                {r.reason && <div className="text-xs text-slate-500 mt-1">{r.reason}</div>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-mono-rs text-sm tracking-[0.2em] uppercase text-slate-400 mb-4 flex items-center gap-2"><ArrowLeftRight className="w-4 h-4 text-[#D902EE]" /> Swaps</h3>
          <div className="space-y-3" data-testid="my-swaps-list">
            {swaps.length === 0 && <p className="text-slate-500 text-sm">No swap requests yet.</p>}
            {swaps.map((r) => {
              const incoming = r.target_name && r.status === "pending" && r.target_tech_id === user.id;
              return (
                <div key={r.id} data-testid={`my-swap-${r.id}`} className="bg-[#0F1219] border border-slate-800 border-l-2 border-l-[#D902EE] p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white">{r.requester_name} → {r.target_name}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  {r.shift && (
                    <div className="flex items-center gap-2">
                      <ModalityBadge modality={r.shift.modality} />
                      <span className="font-mono-rs text-xs text-slate-400">{r.shift.date} {r.shift.start_time}–{r.shift.end_time}</span>
                    </div>
                  )}
                  {incoming && (
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`accept-swap-${r.id}`} onClick={() => decideSwap(r.id, "approved")}
                        className="flex-1 py-1.5 text-xs font-mono-rs uppercase border border-[#00FF66]/50 text-[#00FF66] hover:bg-[#00FF66]/10 transition-all">Accept</button>
                      <button data-testid={`reject-swap-${r.id}`} onClick={() => decideSwap(r.id, "denied")}
                        className="flex-1 py-1.5 text-xs font-mono-rs uppercase border border-[#FF003C]/50 text-[#FF003C] hover:bg-[#FF003C]/10 transition-all">Reject</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <TimeOffModal open={toOpen} onOpenChange={setToOpen} onSaved={load} />
      <SwapModal open={swapOpen} onOpenChange={setSwapOpen} onSaved={load} myShifts={myShifts} user={user} />
    </div>
  );
}

function TimeOffModal({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "" });
  useEffect(() => { if (open) setForm({ start_date: "", end_date: "", reason: "" }); }, [open]);
  const inputCls = "w-full bg-[#05060A] border border-slate-700 px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF] transition-colors";
  const submit = async () => {
    if (!form.start_date || !form.end_date) return toast.error("Select dates");
    try { await api.post("/timeoff", form); toast.success("Time-off requested"); onOpenChange(false); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1219] border border-[#00F0FF]/40 text-white rounded-none max-w-md" data-testid="timeoff-modal">
        <DialogHeader><DialogTitle className="font-mono-rs uppercase tracking-wider">Request Time Off</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">From</label>
              <input data-testid="timeoff-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} /></div>
            <div><label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">To</label>
              <input data-testid="timeoff-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} /></div>
          </div>
          <div><label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Reason</label>
            <input data-testid="timeoff-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={inputCls} placeholder="Optional" /></div>
        </div>
        <DialogFooter>
          <button data-testid="timeoff-submit" onClick={submit} className="w-full py-2 font-mono-rs uppercase tracking-wider text-sm border border-[#00F0FF] bg-[#00F0FF]/10 hover:bg-[#00F0FF]/25 transition-all duration-300">Submit</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SwapModal({ open, onOpenChange, onSaved, myShifts, user }) {
  const [shiftId, setShiftId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [note, setNote] = useState("");
  const [colleagues, setColleagues] = useState([]);

  useEffect(() => {
    if (open) {
      setShiftId(""); setTargetId(""); setNote("");
      // colleagues of same category (eligible to cover)
      api.get("/staff/colleagues").then((r) => setColleagues(r.data)).catch(() => toast.error("Could not load colleagues"));
    }
  }, [open]);

  const mine = myShifts.filter((s) => s.tech_id === user.id);
  const inputCls = "w-full bg-[#05060A] border border-slate-700 px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF] transition-colors";

  const submit = async () => {
    if (!shiftId || !targetId) return toast.error("Select a shift and colleague");
    try { await api.post("/swaps", { shift_id: shiftId, target_tech_id: targetId, note }); toast.success("Swap requested"); onOpenChange(false); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1219] border border-[#D902EE]/40 text-white rounded-none max-w-md" data-testid="swap-modal">
        <DialogHeader><DialogTitle className="font-mono-rs uppercase tracking-wider">Request Shift Swap</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div><label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Your Shift</label>
            <select data-testid="swap-shift-select" value={shiftId} onChange={(e) => setShiftId(e.target.value)} className={inputCls}>
              <option value="">— Select shift —</option>
              {mine.map((s) => <option key={s.id} value={s.id}>{s.date} {s.start_time}–{s.end_time} ({s.modality})</option>)}
            </select>
            {mine.length === 0 && <p className="text-xs text-[#FFB800] mt-1">You have no upcoming shifts to swap.</p>}
          </div>
          <div><label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Swap With ({user.category} tech)</label>
            <select data-testid="swap-target-select" value={targetId} onChange={(e) => setTargetId(e.target.value)} className={inputCls}>
              <option value="">— Select colleague —</option>
              {colleagues.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Note</label>
            <input data-testid="swap-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="Optional" /></div>
        </div>
        <DialogFooter>
          <button data-testid="swap-submit" onClick={submit} className="w-full py-2 font-mono-rs uppercase tracking-wider text-sm border border-[#D902EE] bg-[#D902EE]/10 hover:bg-[#D902EE]/25 transition-all duration-300">Submit</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
